import React from 'react'
import { message, Tooltip, Tag, Modal, Input } from 'antd'
import Button from '../../components/common/button/button'
import {
  PaperClipOutlined,
  BankOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  BellOutlined,
  LoadingOutlined
} from '@ant-design/icons'
import { Switch } from 'antd'
import './rfp.scss'
import {
  fetchRFPList,
  deleteRFPDraft,
  approveRejectRFP,
  fetchRFPDraftById,
  updateRfpPaymentStatus
} from '../../api/rfpApiService/rfpApiUtils'

import { getSymbol } from '../../utils/currencyUtils'

const { TextArea } = Input

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
export const STATUS_COLORS = {
  approved: 'green',
  rejected: 'red',
  partial: 'orange',
  pending: 'blue',
  default: 'default'
}

export const STATUS_FILTERS = [
  { text: 'Approved', value: 'Approved' },
  { text: 'Rejected', value: 'Rejected' },
  { text: 'Partial', value: 'Partial' },
  { text: 'Pending', value: 'Pending' }
]

export const PAGE_SIZE_OPTIONS = ['5', '10', '20', '50']

// ---------------------------------------------------------------------------
// Helper Functions
// ---------------------------------------------------------------------------
export const getVendorName = (selectedVendor) => {
  if (selectedVendor && typeof selectedVendor === 'object') {
    return selectedVendor.vendorName || 'Unknown Vendor'
  }
  return 'Unknown Vendor'
}

export const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('en-GB')
}

export const getCostCenter = (costCenterList) => {
  if (!costCenterList) return ''

  if (Array.isArray(costCenterList)) {
    return costCenterList.length > 0 ? (costCenterList[0].costCenter || '') : ''
  }

  try {
    const parsed = JSON.parse(costCenterList)
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed[0].costCenter || ''
    }
  } catch (e) {
    console.warn('Failed to parse cost center list:', e)
  }
  return ''
}

export const getPurpose = (costCenterList) => {
  if (!costCenterList) return ''

  if (Array.isArray(costCenterList)) {
    return costCenterList.length > 0 ? (costCenterList[0].purpose || '') : ''
  }

  try {
    const parsed = JSON.parse(costCenterList)
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed[0].purpose || ''
    }
  } catch (e) {
    console.warn('Failed to parse cost center list:', e)
  }
  return ''
}

export const getStatusColor = (status) => {
  return STATUS_COLORS[status?.toLowerCase()] || STATUS_COLORS.default
}

export const renderHeaderWithTooltip = (title, maxLength = 15) => {
  if (title.length <= maxLength) {
    return title
  }

  return (
    <Tooltip title={title} placement="top">
      <span>{title.substring(0, maxLength)}...</span>
    </Tooltip>
  )
}

export const createEmptyState = (activeTab) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px',
      padding: '40px 20px',
      color: '#8c8c8c'
    }}
  >
    <svg
      width="80"
      height="80"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ marginBottom: '16px', opacity: 0.6 }}
    >
      <rect x="15" y="25" width="50" height="35" rx="3" stroke="#d9d9d9" strokeWidth="2" fill="none" />
      <rect x="20" y="30" width="40" height="25" rx="1" stroke="#d9d9d9" strokeWidth="1.5" fill="none" />
      <path d="M25 20 L35 15 L45 15 L55 20" stroke="#d9d9d9" strokeWidth="2" fill="none" />
      <circle cx="30" cy="40" r="2" fill="#d9d9d9" />
      <circle cx="40" cy="40" r="2" fill="#d9d9d9" />
      <circle cx="50" cy="40" r="2" fill="#d9d9d9" />
    </svg>
    <span style={{ fontSize: '16px', fontWeight: 500 }}>
      {activeTab === 'drafts' ? 'No drafts available' : 'No RFP data found'}
    </span>
    <span style={{ fontSize: '14px', marginTop: '4px', opacity: 0.8 }}>
      {activeTab === 'drafts'
        ? 'Create your first draft to get started'
        : 'Create your first RFP to get started'}
    </span>
  </div>
)

// Capitalize each word in a name (e.g., "rajashree nayak" → "Rajashree Nayak")
export const capitalizeName = (name) => {
  if (!name || typeof name !== 'string') return 'N/A'
  return name
    .trim()
    .toLowerCase()
    .split(' ')
    .map(word => word ? word.charAt(0).toUpperCase() + word.slice(1) : '')
    .join(' ')
    .replace(/\s+/g, ' ')
}

// ---------------------------------------------------------------------------
// Approve / Reject Handlers
// ---------------------------------------------------------------------------
export const handleApproveRFP = (record, msalInstance, onRefresh) => {
  let isLoading = false

  const modal = Modal.confirm({
    title: 'Approve RFP',
    icon: <ExclamationCircleOutlined />,
    content: (
      <div>
        <p>Are you sure you want to approve this RFP?</p>
        <div style={{ marginTop: '12px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
          <p style={{ margin: '4px 0' }}>
            <strong>RFP ID:</strong> {record.rfpId}
          </p>
          <p style={{ margin: '4px 0' }}>
            <strong>Vendor:</strong> {record.vendorName}
          </p>
          <p style={{ margin: '4px 0' }}>
            <strong>Amount:</strong> {record.currencySymbol}
            {record.requestedAmount.toLocaleString()}
          </p>
        </div>
      </div>
    ),
    footer: null
  })

  const updateFooter = () => {
    modal.update({
      footer: (
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button disabled={isLoading} onClick={() => modal.destroy()}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={isLoading}
            onClick={async () => {
              try {
                isLoading = true
                updateFooter()

                await approveRejectRFP(record.rfpId, 'approve', '', msalInstance)
                message.success(`RFP ${record.rfpId} has been approved successfully`)
                modal.destroy()
                onRefresh?.()
              } catch (error) {
                message.error(error.response?.data?.message || 'Failed to approve RFP.')
              } finally {
                isLoading = false
                updateFooter()
              }
            }}
          >
            Approve
          </Button>
        </div>
      )
    })
  }

  updateFooter()
}

export const handleRejectRFP = (record, msalInstance, onRefresh) => {
  let rejectionReason = ''
  let isLoading = false

  const modal = Modal.confirm({
    title: 'Reject RFP',
    icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
    width: 520,
    content: (
      <div>
        <p>Are you sure you want to reject this RFP?</p>

        <div
          style={{
            marginTop: '12px',
            padding: '12px',
            background: '#fff2f0',
            borderRadius: '4px',
            border: '1px solid #ffccc7'
          }}
        >
          <p style={{ margin: '4px 0' }}>
            <strong>RFP ID:</strong> {record.rfpId}
          </p>
          <p style={{ margin: '4px 0' }}>
            <strong>Vendor:</strong> {record.vendorName}
          </p>
          <p style={{ margin: '4px 0' }}>
            <strong>Amount:</strong> {record.currencySymbol}
            {record.requestedAmount.toLocaleString()}
          </p>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            Reason for Rejection <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <TextArea
            rows={4}
            maxLength={500}
            showCount
            placeholder="Please provide a reason for rejecting this RFP..."
            onChange={(e) => (rejectionReason = e.target.value)}
          />
        </div>
      </div>
    ),
    footer: null
  })

  const updateFooter = () => {
    modal.update({
      footer: (
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button disabled={isLoading} onClick={() => modal.destroy()}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            loading={isLoading}
            onClick={async () => {
              if (!rejectionReason?.trim()) {
                message.error('Please provide a reason for rejection')
                return
              }

              try {
                isLoading = true
                updateFooter()

                await approveRejectRFP(
                  record.rfpId,
                  'reject',
                  rejectionReason.trim(),
                  msalInstance
                )
                message.success(`RFP ${record.rfpId} has been rejected`)
                modal.destroy()
                onRefresh?.()
              } catch (error) {
                message.error(error.response?.data?.message || 'Failed to reject RFP.')
              } finally {
                isLoading = false
                updateFooter()
              }
            }}
          >
            Reject
          </Button>
        </div>
      )
    })
  }

  updateFooter()
}

export const handlePaymentStatusChange = async (
  record,
  newStatus,
  msalInstance,
  onRefresh,
  userRole
) => {
  try {
    if (userRole !== 'Finance Manager') {
      message.error('Only Finance Manager can change the payment status')
      return false
    }

    await updateRfpPaymentStatus(record.rfpId, newStatus ? 'Y' : 'N', msalInstance)
    message.success(`Payment status updated to ${newStatus ? 'Paid' : 'Not Paid'}`)

    if (onRefresh) onRefresh()

    return true
  } catch (error) {
    message.error(error.response?.data?.message || 'Failed to update payment status')
    return false
  }
}

// Split a flat Excel blob into per-store sheets + an "All Stores" summary sheet
const splitBlobByStore = async (blob) => {
  const ExcelJS = require('exceljs')
  const arrayBuffer = await blob.arrayBuffer()

  const srcWorkbook = new ExcelJS.Workbook()
  await srcWorkbook.xlsx.load(arrayBuffer)
  const srcSheet = srcWorkbook.worksheets[0]

  // Collect header values (0-indexed plain array)
  const headerValues = []
  srcSheet.getRow(1).eachCell({ includeEmpty: true }, (cell) => {
    headerValues.push(cell.value)
  })

  // Group rows by Store ID (first column)
  const storeDataMap = {}
  const allRowValues = []
  srcSheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return
    const rowArr = []
    row.eachCell({ includeEmpty: true }, (cell) => {
      rowArr.push(cell.value)
    })
    allRowValues.push(rowArr)
    const storeId = String(row.getCell(1).value ?? '').trim()
    if (!storeId) return
    if (!storeDataMap[storeId]) storeDataMap[storeId] = []
    storeDataMap[storeId].push(rowArr)
  })

  // Build new workbook
  const newWorkbook = new ExcelJS.Workbook()

  // "All Stores" sheet first
  const allSheet = newWorkbook.addWorksheet('All Stores')
  allSheet.addRow(headerValues)
  for (const rowArr of allRowValues) allSheet.addRow(rowArr)

  // One sheet per store, sorted numerically then alphabetically
  const sortedStores = Object.keys(storeDataMap).sort(
    (a, b) => Number(a) - Number(b) || a.localeCompare(b)
  )
  for (const storeId of sortedStores) {
    const sheet = newWorkbook.addWorksheet(`Store ${storeId}`)
    sheet.addRow(headerValues)
    for (const rowArr of storeDataMap[storeId]) sheet.addRow(rowArr)
  }

  const buffer = await newWorkbook.xlsx.writeBuffer()
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
}

// Add "Kmart Approved On" and "Ayala Approved On" columns to an Excel blob
const injectApprovalDateColumns = async (blob, dateMap) => {
  const ExcelJS = require('exceljs')
  const arrayBuffer = await blob.arrayBuffer()

  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(arrayBuffer)

  workbook.worksheets.forEach(sheet => {
    const headerRow = sheet.getRow(1)

    // Find RFP ID column by header text
    let rfpIdColIndex = -1
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const val = String(cell.value || '').toLowerCase().replace(/[\s_\-]/g, '')
      if (rfpIdColIndex === -1 && (val === 'rfpid' || val === 'rfpno' || val === 'transactionid')) {
        rfpIdColIndex = colNumber
      }
    })

    if (rfpIdColIndex === -1) return

    let maxCol = 0
    headerRow.eachCell({ includeEmpty: false }, (_, colNumber) => {
      if (colNumber > maxCol) maxCol = colNumber
    })

    const kmartCol = maxCol + 1
    const ayalaCol = maxCol + 2

    headerRow.getCell(kmartCol).value = 'Kmart Approved On'
    headerRow.getCell(ayalaCol).value = 'Ayala Approved On'

    sheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return
      const rfpId = String(row.getCell(rfpIdColIndex).value || '').trim()
      if (rfpId && dateMap[rfpId]) {
        row.getCell(kmartCol).value = dateMap[rfpId].kmartApprovedOn
        row.getCell(ayalaCol).value = dateMap[rfpId].ayalaApprovedOn
      }
    })
  })

  const outBuffer = await workbook.xlsx.writeBuffer()
  return new Blob([outBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
}

// Download RFP as Excel
export const handleDownloadRFP = async (msalInstance, dateRange = null, allStores = false) => {
  const loadingMsg = allStores
    ? 'Preparing Excel download for all stores...'
    : 'Preparing Excel download...'
  const hide = message.loading(loadingMsg, 0)
  try {
    const { exportRFPExcel, fetchRFPList } = require('../../api/rfpApiService/rfpApiUtils')

    const params = { allStores }
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.startDate = dateRange[0].format('YYYY-MM-DD')
      params.endDate = dateRange[1].format('YYYY-MM-DD')
    }

    // Best-effort: paginate through all records to build a complete approval-date lookup
    const dateMap = {}
    const PAGE_LIMIT = 100
    try {
      const addToMap = (items) => {
        items.forEach(item => {
          const rfpId = item.transactionId
          if (rfpId) {
            const approval = item.transactionApprovalId || {}
            dateMap[rfpId] = {
              kmartApprovedOn: approval.groupAApprovedAt ? formatDate(approval.groupAApprovedAt) : '',
              ayalaApprovedOn: approval.groupBApprovedAt ? formatDate(approval.groupBApprovedAt) : ''
            }
          }
        })
      }

      const firstResp = await fetchRFPList(msalInstance, { page: 1, limit: PAGE_LIMIT })
      if (firstResp?.data) {
        addToMap(firstResp.data)
        const total = firstResp.pagination?.total ?? firstResp.totalRecords ?? firstResp.data.length
        const totalPages = Math.ceil(total / PAGE_LIMIT)
        if (totalPages > 1) {
          const pageNums = Array.from({ length: totalPages - 1 }, (_, i) => i + 2)
          const remaining = await Promise.all(
            pageNums.map(p => fetchRFPList(msalInstance, { page: p, limit: PAGE_LIMIT }))
          )
          remaining.forEach(resp => resp?.data && addToMap(resp.data))
        }
      }
    } catch (e) {
      console.warn('Could not fetch approval dates for export; columns will be empty.', e)
    }

    let blob = await exportRFPExcel(msalInstance, params)

    // Inject approval date columns before any further processing
    try {
      blob = await injectApprovalDateColumns(blob, dateMap)
    } catch (e) {
      console.warn('Could not inject approval date columns; exporting without them.', e)
    }

    // When downloading all stores, split into per-store tabs
    if (allStores) {
      blob = await splitBlobByStore(blob)
    }

    const date = new Date()
    const dateSuffix = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    const filename = allStores
      ? `RFP_All_Stores_${dateSuffix}.xlsx`
      : `RFP_List_${dateSuffix}.xlsx`

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    message.success(allStores ? 'All stores RFP downloaded successfully' : 'RFP list downloaded successfully')
    return true
  } catch (error) {
    console.error('Error downloading RFP Excel:', error)
    message.error(error.message || 'Failed to download RFP list')
    throw error
  } finally {
    hide()
  }
}

// ---------------------------------------------------------------------------
// Data Transformation
// ---------------------------------------------------------------------------
export const transformApiData = (data) => {
  return data.map((item, index) => {
    const approvalData = item.transactionApprovalId || {}

    const getApproverStatus = (action, approverName) => {
      if (!action) return 'Pending'
      switch (action.toLowerCase()) {
        case 'approve':
          return approverName || 'Approved'
        case 'reject':
          return approverName || 'Rejected'
        default:
          return 'Pending'
      }
    }

    const getApproverDisplay = (action, approverName) => {
      if (!action) {
        return {
          name: 'Pending',
          status: null,
          color: null
        }
      }

      const status = action.toLowerCase()
      return {
        name: approverName || (status === 'approve' ? 'Approved' : 'Rejected'),
        status: status,
        color: status === 'approve' ? '#52c41a' : '#ff4d4f'
      }
    }

    // Capitalize names
    const createdByName = item.createdBy?.userName ? capitalizeName(item.createdBy.userName) : 'N/A'

    const groupAApproverNameRaw = approvalData.groupAApproverId?.userName
    const groupBApproverNameRaw = approvalData.groupBApproverId?.userName

    const groupAApproverName = groupAApproverNameRaw ? capitalizeName(groupAApproverNameRaw) : null
    const groupBApproverName = groupBApproverNameRaw ? capitalizeName(groupBApproverNameRaw) : null

    const kmartApproverDisplay = getApproverDisplay(approvalData.groupAAction, groupAApproverName)
    const ayalaApproverDisplay = getApproverDisplay(approvalData.groupBAction, groupBApproverName)

    const kmartApprover = getApproverStatus(approvalData.groupAAction, groupAApproverName)
    const ayalaApprover = getApproverStatus(approvalData.groupBAction, groupBApproverName)

    const currencyCode = item.currencyType === 'Others' && item.otherCurrencyType
      ? item.otherCurrencyType.toUpperCase()
      : (item.currencyType || 'PHP').toUpperCase()

    const currencySymbol = getSymbol(currencyCode)

    return {
      id: item._id || item.id,
      srNo: index + 1,
      rfpId: item.transactionId || item.rfpId,
      vendorName: getVendorName(item.selectedVendor),
      vendorId: item.selectedVendor?.vendorID || 'N/A',
      vendorAddress: item.selectedVendor?.vendorAddress || 'N/A',
      tinNumber: item.selectedVendor?.tinNumber || 'N/A',
      requestedAmount: parseFloat(item.amount) || 0,
      dateOfCreation: formatDate(item.transactionDateTime),
      createdBy: createdByName,
      createdByEmail: item.createdBy?.emailId || '',
      categories: item.transactionCategories || '',
      invoiceReferenceNumber: item.invoiceReferenceNumber || '',
      costCenter: getCostCenter(item.costCenterPurposeList),
      purpose: getPurpose(item.costCenterPurposeList),
      costCenterData: {
        costCenterPurposeList: item.costCenterPurposeList || [],
        amount: parseFloat(item.amount) || 0
      },
      approver1: kmartApprover,
      approver2: ayalaApprover,
      kmartApproverDisplay,
      ayalaApproverDisplay,
      preferredPaymentMethod: item.preferredPaymentMethod || '',
      filePreparedFor: item.filePreparedFor || '',
      status: approvalData.approvalStatus || 'Pending',
      approvedDate: (() => {
        const a = approvalData.groupAApprovedAt
        const b = approvalData.groupBApprovedAt
        if (a && b) return new Date(a) > new Date(b) ? a : b
        return a || b || null
      })(),
      kmartActionedOn: approvalData.groupAApprovedAt || null,
      ayalaActionedOn: approvalData.groupBApprovedAt || null,
      paymentStatus: item.paymentStatus || 'N',
      attachments: item.attachments || [],
      saveAsDraft: item.saveAsDraft === true || String(item.saveAsDraft).toLowerCase() === 'true',
      filePreparedBy: item.filePreparedBy || '',
      getApproval: item.getApproval || false,
      currencyType: item.currencyType || '',
      otherCurrencyType: item.otherCurrencyType || '',
      currencySymbol,
      approvalDetails: {
        groupAAction: approvalData.groupAAction,
        groupBAction: approvalData.groupBAction,
        groupAApprover: groupAApproverName,
        groupBApprover: groupBApproverName,
        groupAComment: approvalData.groupAComment,
        groupBComment: approvalData.groupBComment,
        groupAApprovedAt: approvalData.groupAApprovedAt,
        groupBApprovedAt: approvalData.groupBApprovedAt
      }
    }
  })
}

export const transformDraftData = (data) => {
  return data.map((item, index) => {
    const currencyCode = item.currencyType === 'Others' && item.otherCurrencyType
      ? item.otherCurrencyType.toUpperCase()
      : (item.currencyType || 'PHP').toUpperCase()

    const currencySymbol = getSymbol(currencyCode)

    const createdByName = item.createdBy?.userName ? capitalizeName(item.createdBy.userName) : 'N/A'

    return {
      id: item._id || item.id,
      rfpId: item.transactionId || item.rfpId || `DRAFT-${item._id?.slice(-6) || index}`,
      vendorName: getVendorName(item.selectedVendor),
      vendorId: item.selectedVendor?.vendorID || 'N/A',
      vendorAddress: item.selectedVendor?.vendorAddress || 'N/A',
      tinNumber: item.selectedVendor?.tinNumber || 'N/A',
      requestedAmount: parseFloat(item.amount) || 0,
      dateCreated: formatDate(item.transactionDateTime),
      categories: item.transactionCategories || '',
      invoiceReferenceNumber: item.invoiceReferenceNumber || '',
      filePreparedFor: item.filePreparedFor || '',
      preferredPaymentMethod: item.preferredPaymentMethod || '',
      createdBy: createdByName,
      createdByEmail: item.createdBy?.emailId || '',
      status: 'Draft',
      lastModified: formatDate(item.updatedAt) || formatDate(item.transactionDateTime),
      attachments: item.attachments || [],
      costCenter: getCostCenter(item.costCenterPurposeList),
      purpose: getPurpose(item.costCenterPurposeList),
      costCenterData: {
        costCenterPurposeList: item.costCenterPurposeList || [],
        amount: parseFloat(item.amount) || 0
      },
      currencyType: item.currencyType || '',
      otherCurrencyType: item.otherCurrencyType || '',
      currencySymbol
    }
  })
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------
export const fetchRfpData = async (
  msalInstance,
  page = 1,
  limit = 10,
  setLoading,
  setRfpData,
  setTotalRecords
) => {
  try {
    setLoading(true)
    const response = await fetchRFPList(msalInstance, { page, limit, drafts: false })

    if (response?.data) {
      const transformedData = transformApiData(response.data)
      setRfpData(transformedData)

      if (response.pagination) {
        setTotalRecords(response.pagination.total)
      } else if (response.totalRecords !== undefined) {
        setTotalRecords(response.totalRecords)
      } else {
        setTotalRecords(response.data.length)
      }
    }
  } catch (error) {
    console.error('Error fetching RFP data:', error)
    message.error('Failed to fetch RFP data. Please try again.')
  } finally {
    setLoading(false)
  }
}

export const fetchDraftsData = async (
  msalInstance,
  page = 1,
  limit = 10,
  setDraftsLoading,
  setDrafts,
  setTotalDrafts
) => {
  try {
    setDraftsLoading(true)
    const response = await fetchRFPList(msalInstance, { page, limit, drafts: true })

    if (response?.data) {
      const transformedData = transformDraftData(response.data)
      setDrafts(transformedData)

      if (response.pagination) {
        setTotalDrafts(response.pagination.total)
      } else if (response.totalRecords !== undefined) {
        setTotalDrafts(response.totalRecords)
      } else {
        setTotalDrafts(response.data.length)
      }
    }
  } catch (error) {
    console.error('Error fetching drafts data:', error)
    message.error('Failed to fetch drafts data. Please try again.')
  } finally {
    setDraftsLoading(false)
  }
}
export const handleSearchRFP = async (
  searchTerm,
  msalInstance,
  setLoading,
  setRfpData,
  setTotalRecords,
  setIsSearchActive
) => {
  if (!searchTerm?.trim()) return

  try {
    setLoading(true)
    setIsSearchActive(true)

    const { searchRFP } = require('../../api/rfpApiService/rfpApiUtils')
    const response = await searchRFP(msalInstance, searchTerm.trim())

    if (response?.data) {
      const transformedData = transformApiData(response.data)
      setRfpData(transformedData)
      setTotalRecords(transformedData.length)
    } else {
      setRfpData([])
      setTotalRecords(0)
    }
  } catch (error) {
    console.error('Error searching RFP:', error)
    message.error('Failed to search RFP. Please try again.')
  } finally {
    setLoading(false)
  }
}
// ---------------------------------------------------------------------------
// Event Handlers (unchanged – included for completeness)
// ---------------------------------------------------------------------------
export const handleTabChange = (key, setActiveTab, setCurrentPage, pageSize, fetchDraftsData, fetchRfpData) => {
  setActiveTab(key)
  setCurrentPage(1)

  if (key === 'drafts') {
    fetchDraftsData(1, pageSize)
  } else if (key === 'rfp') {
    fetchRfpData(1, pageSize)
  }
}

export const handleCostCenterClick = (record, setSelectedCostCenterData, setSelectedVendorName, setCostCenterModalVisible) => {
  setSelectedCostCenterData(record.costCenterData)
  setSelectedVendorName(record.vendorName)
  setCostCenterModalVisible(true)
}

export const handleAddRFP = (setEditingDraft, setIsFormVisible) => {
  setEditingDraft(null)
  setIsFormVisible(true)
}

export const handleFormCancel = (setEditingDraft, setIsFormVisible) => {
  setEditingDraft(null)
  setIsFormVisible(false)
}

export const handleFormSubmit = async (
  formData,
  currentPage,
  pageSize,
  editingDraft,
  activeTab,
  setRfpData,
  setTotalRecords,
  setDrafts,
  setTotalDrafts,
  setIsFormVisible,
  setEditingDraft,
  setCurrentPage,
  fetchRfpData,
  fetchDraftsData
) => {
  try {
    setIsFormVisible(false)
    setEditingDraft(null)

    // Determine if we're converting a draft
    const isConvertingDraft = editingDraft && (editingDraft.transactionId || editingDraft._id)

    // Create the new RFP object for optimistic UI update
    const newRFP = {
      id: formData.apiResponse?._id || Date.now(),
      srNo: (currentPage - 1) * pageSize + 1,
      rfpId: formData.rfpId || formData.apiResponse?.transactionId,
      vendorName: formData.vendorName,
      requestedAmount: formData.requestedAmount,
      dateOfCreation: formData.dateOfCreation,
      createdBy: formData.createdBy || 'N/A',
      createdByEmail: formData.createdByEmail || '',
      categories: formData.categories,
      costCenter: formData.costCenter,
      purpose: formData.purpose,
      costCenterData: {
        costCenterPurposeList: formData.costCenterData || [],
        amount: formData.requestedAmount
      },
      invoiceReferenceNumber: formData.invoiceReference || '',
      approver1: 'Pending',
      approver2: 'Pending',
      kmartApproverDisplay: {
        name: 'Pending',
        status: null,
        color: null
      },
      ayalaApproverDisplay: {
        name: 'Pending',
        status: null,
        color: null
      },
      preferredPaymentMethod: formData.preferredPaymentMethod,
      filePreparedFor: formData.filePreparedFor,
      status: formData.status || 'Pending',
      paymentStatus: 'N',
      attachments: formData.attachments || [],
      filePreparedBy: formData.filePreparedBy || '',
      getApproval: formData.apiResponse?.getApproval || false,
      currencyType: formData.currencyType || formData.currency || 'PHP',
      otherCurrencyType: formData.otherCurrencyType || '',
      currencySymbol: getSymbol(formData.currency || 'PHP'),
      approvalDetails: {
        groupAAction: null,
        groupBAction: null,
        groupAApprover: null,
        groupBApprover: null,
        groupAComment: null,
        groupBComment: null,
        groupAApprovedAt: null,
        groupBApprovedAt: null
      }
    }

    // Optimistic UI update for RFP list
    setRfpData((prev) => [newRFP, ...prev.slice(0, pageSize - 1)])
    setTotalRecords((prev) => prev + 1)

    // If converting a draft, remove it from drafts list
    if (isConvertingDraft) {
      setDrafts((prev) => prev.filter((draft) =>
        draft.id !== editingDraft.id &&
        draft.rfpId !== editingDraft.rfpId &&
        draft.transactionId !== editingDraft.transactionId
      ))
      setTotalDrafts((prev) => Math.max(0, prev - 1))
    }

    // Reset to first page
    setCurrentPage(1)

    // Fetch fresh data from backend
    if (isConvertingDraft) {
      await fetchRfpData(1, pageSize)
      if (activeTab === 'drafts') {
        await fetchDraftsData(1, pageSize)
      }
    } else {
      if (activeTab === 'rfp') {
        await fetchRfpData(1, pageSize)
      } else if (activeTab === 'drafts') {
        await fetchDraftsData(1, pageSize)
      }
    }

  } catch (error) {
    console.error('Error handling RFP submission:', error)
    message.error('RFP was created but failed to refresh the list. Please refresh the page.')

    // Still try to refresh the data
    try {
      if (activeTab === 'rfp' || editingDraft) {
        await fetchRfpData(currentPage, pageSize)
      }
      if (activeTab === 'drafts' || editingDraft) {
        await fetchDraftsData(currentPage, pageSize)
      }
    } catch (refreshError) {
      console.error('Error refreshing data after failed submission:', refreshError)
    }
  }
}

export const handleSaveAsDraft = async (
  formData,
  editingDraft,
  activeTab,
  pageSize,
  setDrafts,
  setTotalDrafts,
  setIsFormVisible,
  setEditingDraft,
  fetchDraftsData
) => {
  try {
    setIsFormVisible(false)
    setEditingDraft(null)

    if (editingDraft) {
      setDrafts((prev) =>
        prev.map((draft) =>
          draft.id === editingDraft.id
            ? {
              ...draft,
              vendorName: formData.vendorName,
              requestedAmount: formData.requestedAmount,
              categories: formData.categories,
              filePreparedFor: formData.filePreparedFor,
              createdBy: formData.createdBy || draft.createdBy,
              lastModified: new Date().toLocaleString(),
              formData: formData
            }
            : draft
        )
      )
    } else {
      const newDraft = {
        id: Date.now(),
        rfpId: `DRAFT-${String(Date.now()).slice(-3)}`,
        vendorName: formData.vendorName || 'Unnamed Vendor',
        requestedAmount: formData.requestedAmount || 0,
        dateCreated: new Date().toLocaleDateString('en-GB'),
        categories: formData.categories || 'Unspecified',
        filePreparedFor: formData.filePreparedFor || '',
        createdBy: formData.createdBy || 'N/A',
        status: 'Draft',
        lastModified: new Date().toLocaleString(),
        formData: formData,
        currencySymbol: getSymbol(formData.currency || 'PHP')
      }

      setDrafts((prev) => [newDraft, ...prev])
      setTotalDrafts((prev) => prev + 1)
    }

    if (activeTab === 'drafts') {
      await fetchDraftsData(1, pageSize)
    }
    
    message.success(editingDraft ? 'Draft updated successfully' : 'Draft saved successfully')
  } catch (error) {
    message.error('Failed to save draft. Please try again.')
    console.error('Error saving draft:', error)
  }
}

export const handlePageChange = async (
  page,
  size,
  pageSize,
  activeTab,
  setPageSize,
  setCurrentPage,
  fetchDraftsData,
  fetchRfpData
) => {
  if (size !== pageSize) {
    setPageSize(size)
    setCurrentPage(1)
    if (activeTab === 'drafts') {
      await fetchDraftsData(1, size)
    } else {
      await fetchRfpData(1, size)
    }
  } else {
    setCurrentPage(page)
    if (activeTab === 'drafts') {
      await fetchDraftsData(page, size)
    } else {
      await fetchRfpData(page, size)
    }
  }
}

export const handleEditDraft = async (draft, setEditingDraft, setIsFormVisible, msalInstance) => {
try {
    const hideLoading = message.loading('Loading draft details...', 0)

    try {
      const draftId = draft.rfpId || draft.transactionId || draft.id
      const response = await fetchRFPDraftById(msalInstance, draftId)

      if (response?.data) {
        const fullDraftData = {
          ...draft,
          _id: response.data._id,
          transactionId: response.data.transactionId,
          storeId: response.data.storeId,
          transactionDateTime: response.data.transactionDateTime,
          transactionCategories: response.data.transactionCategories,
          invoiceReferenceNumber: response.data.invoiceReferenceNumber,
          currencyType: response.data.currencyType,
          amount: response.data.amount,
          filePreparedFor: response.data.filePreparedFor,
          preferredPaymentMethod: response.data.preferredPaymentMethod,
          selectedVendor: response.data.selectedVendor,
          attachments: response.data.attachments || [],
          costCenterPurposeList: response.data.costCenterPurposeList || [],
          saveAsDraft: response.data.saveAsDraft,
          createdBy: response.data.createdBy,
          createdAt: response.data.createdAt,
          updatedAt: response.data.updatedAt,
          getApproval: response.data.getApproval || false,
          formData: {
            vendorName: response.data.selectedVendor?._id,
            vendorDisplayName: response.data.selectedVendor?.vendorName,
            vendorAddress: response.data.selectedVendor?.vendorAddress,
            vendorId: response.data.selectedVendor?.vendorID,
            tinNo: response.data.selectedVendor?.tinNumber,
            invoiceReference: response.data.invoiceReferenceNumber,
            dateOfCreation: response.data.transactionDateTime,
            categories: response.data.transactionCategories,
            currency: response.data.currencyType,
            requestedAmount: parseFloat(response.data.amount),
            filePreparedFor: response.data.filePreparedFor,
            preferredPaymentMethod: response.data.preferredPaymentMethod,
            attachments: response.data.attachments || [],
            CostCenterData: response.data.costCenterPurposeList || []
          }
        }

        setEditingDraft(fullDraftData)
        setIsFormVisible(true)
      } else {
        throw new Error('Failed to fetch draft details')
      }
    } finally {
      hideLoading()
    }
  } catch (error) {
    console.error('Error loading draft for editing:', error)
    message.error(error.message || 'Failed to load draft details. Please try again.')
  }
}

export const handleDeleteDraft = async (
  draftId,
  activeTab,
  currentPage,
  pageSize,
  setDrafts,
  setTotalDrafts,
  fetchDraftsData,
  msalInstance
) => {
 try {
    if (!draftId) {
      throw new Error('Draft ID is required for deletion')
    }

    const response = await deleteRFPDraft(draftId, msalInstance)

    if (response.message && response.message.includes('deleted successfully')) {
      message.success('Draft deleted successfully')

      if (activeTab === 'drafts') {
        await fetchDraftsData(currentPage, pageSize)
      }

      return response
    } else {
      throw new Error(response.message || 'Failed to delete draft')
    }
  } catch (error) {
    console.error('Error deleting draft:', error)
    message.error(error.message || 'Failed to delete draft')
    throw error
  }
}

export const handleCostCenterModalClose = (
  setCostCenterModalVisible,
  setSelectedCostCenterData,
  setSelectedVendorName
) => {
  setCostCenterModalVisible(false)
  setSelectedCostCenterData(null)
  setSelectedVendorName('')
}

// ---------------------------------------------------------------------------
// Table Columns
// ---------------------------------------------------------------------------
export const createColumns = (
  currentPage,
  pageSize,
  renderHeaderWithTooltip,
  getStatusColor,
  handleCostCenterClick,
  msalInstance,
  onRefresh,
  onAttachmentClick,
  currentUserEmail,
  currentUserName,
  onPaymentStatusChange,
  userRole,
  onRfpIdSearch,
  onSendReminder,
  reminderLoadingByRfpId = {}
) => [
  {
    title: 'Sr. No',
    key: 'srNo',
    width: 80,
    fixed: 'left',
    align: 'center',
    render: (_, __, index) => (currentPage - 1) * pageSize + index + 1
  },
{
  title: 'RFP Id',
  dataIndex: 'rfpId',
  key: 'rfpId',
  width: 150,
  fixed: 'left',
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Input
        placeholder="Search RFP ID"
        value={selectedKeys[0]}
        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => {
          confirm()
          onRfpIdSearch(selectedKeys[0])
        }}
        style={{ marginBottom: 8, display: 'block' }}
        autoFocus
      />
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          type="primary"
          onClick={() => {
            confirm()
            onRfpIdSearch(selectedKeys[0])
          }}
          icon={<SearchOutlined />}
          size="small"
          style={{ flex: 1, backgroundColor: '#ad0270', borderColor: '#ad0270' }}
        >
          Search
        </Button>
        <Button
          onClick={() => {
            clearFilters()
            confirm()
            onRfpIdSearch('')
          }}
          size="small"
          style={{ flex: 1 }}
        >
          Reset
        </Button>
      </div>
    </div>
  ),
  filterIcon: (filtered) => (
    <SearchOutlined style={{ color: filtered ? '#ad0270' : undefined }} />
  )
},
  {
    title: renderHeaderWithTooltip('Vendor Name', 12),
    dataIndex: 'vendorName',
    key: 'vendorName',
    width: 180,
    render: (text) => {
      if (!text) return '-'
      const truncated = text.length > 20
      return (
        <Tooltip title={truncated ? text : ''} placement="top">
          <span>{truncated ? `${text.substring(0, 20)}...` : text}</span>
        </Tooltip>
      )
    }
  },
  {
    title: renderHeaderWithTooltip('Requested Amount', 12),
    dataIndex: 'requestedAmount',
    key: 'requestedAmount',
    width: 150,
    render: (amount, record) => `${record.currencySymbol}${amount.toLocaleString()}`
  },
  {
    title: renderHeaderWithTooltip('Date of Creation', 12),
    dataIndex: 'dateOfCreation',
    key: 'dateOfCreation',
    width: 130
  },
  {
    title: 'Created By',
    dataIndex: 'createdBy',
    key: 'createdBy',
    width: 150
  },
  {
    title: 'Categories',
    dataIndex: 'categories',
    key: 'categories',
    width: 200,
    ellipsis: true
  },
  {
    title: renderHeaderWithTooltip('Invoice Ref No', 12),
    dataIndex: 'invoiceReferenceNumber',
    key: 'invoiceReferenceNumber',
    width: 150,
    ellipsis: true,
    render: (text) => text || 'N/A'
  },
  {
    title: renderHeaderWithTooltip('File Prepared For', 12),
    dataIndex: 'filePreparedFor',
    key: 'filePreparedFor',
    width: 150,
    ellipsis: true,
    render: (text) => {
      if (!text) return '-'
      return (
        <Tooltip title={text} placement="top">
          <span>{text}</span>
        </Tooltip>
      )
    }
  },
  {
    title: renderHeaderWithTooltip('Kmart Approver', 12),
    dataIndex: 'approver1',
    key: 'approver1',
    width: 132,
    render: (_, record) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {record.kmartApproverDisplay?.status ? (
          <Tooltip
            title={
              record.kmartApproverDisplay.status === 'approve'
                ? `Approved by ${record.kmartApproverDisplay?.name || record.approver1}`
                : `Rejected by ${record.kmartApproverDisplay?.name || record.approver1}`
            }
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: record.kmartApproverDisplay.color,
                  flexShrink: 0
                }}
              />
              <span>{record.kmartApproverDisplay?.name || record.approver1}</span>
            </div>
          </Tooltip>
        ) : (
          <span>{record.kmartApproverDisplay?.name || record.approver1}</span>
        )}
      </div>
    )
  },
  {
    title: renderHeaderWithTooltip('Ayala Approver', 12),
    dataIndex: 'approver2',
    key: 'approver2',
    width: 132,
    render: (_, record) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {record.ayalaApproverDisplay?.status ? (
          <Tooltip
            title={
              record.ayalaApproverDisplay.status === 'approve'
                ? `Approved by ${record.ayalaApproverDisplay?.name || record.approver2}`
                : `Rejected by ${record.ayalaApproverDisplay?.name || record.approver2}`
            }
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: record.ayalaApproverDisplay.color,
                  flexShrink: 0
                }}
              />
              <span>{record.ayalaApproverDisplay?.name || record.approver2}</span>
            </div>
          </Tooltip>
        ) : (
          <span>{record.ayalaApproverDisplay?.name || record.approver2}</span>
        )}
      </div>
    )
  },
  {
    title: renderHeaderWithTooltip('Preferred Payment Method', 15),
    dataIndex: 'preferredPaymentMethod',
    key: 'preferredPaymentMethod',
    width: 180
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    filters: STATUS_FILTERS,
    onFilter: (value, record) => record.status === value,
    render: (status) => (
      <Tag color={getStatusColor(status)} className="status-tag-with-border">
        {status}
      </Tag>
    )
  },
  {
    title: renderHeaderWithTooltip('Kmart- Approved On', 12),
    dataIndex: 'kmartActionedOn',
    key: 'kmartActionedOn',
    width: 150,
    render: (date) => date ? formatDate(date) : '-'
  },
  {
    title: renderHeaderWithTooltip('Ayala-  Approved On', 12),
    dataIndex: 'ayalaActionedOn',
    key: 'ayalaActionedOn',
    width: 150,
    render: (date) => date ? formatDate(date) : '-'
  },
  {
    title: renderHeaderWithTooltip('Payment Status', 12),
    dataIndex: 'paymentStatus',
    key: 'paymentStatus',
    width: 130,
    align: 'center',
    render: (paymentStatus, record) => {
      const isFinanceManager = userRole === 'Finance Manager'
      const isPaid = paymentStatus === 'Y'

      const isApproved = record.status === 'Approved'
      const isPendingOrPartial = ['Pending', 'Partial'].includes(record.status)
      const isRejected = record.status === 'Rejected'

      let tooltipMessage = ''
      let isDisabled = true

      if (!isFinanceManager) {
        tooltipMessage = 'Only Finance Manager can update payment status'
      } else if (isRejected) {
        tooltipMessage = 'Cannot mark as paid: RFP was rejected'
      } else if (isPendingOrPartial) {
        tooltipMessage = 'Approvals pending: Kmart & Ayala must approve first'
      } else if (isApproved && isPaid) {
        tooltipMessage = 'Already marked as Paid — action is final'
      } else if (isApproved && !isPaid) {
        tooltipMessage = 'Click to mark as Paid'
        isDisabled = false
      } else {
        tooltipMessage = 'Unknown state — contact admin'
      }

      return (
        <Tooltip title={tooltipMessage} placement="top">
          <span>
            <Switch
              className="rfp-payment-status-switch"
              checked={isPaid}
              onChange={(checked) =>
                !isDisabled &&
                onPaymentStatusChange(record, checked, msalInstance, onRefresh, userRole)
              }
              disabled={isDisabled}
              checkedChildren="Paid"
              unCheckedChildren="Unpaid"
              style={{
                backgroundColor: isPaid ? '#52c41a' : '#d9d9d9',
                opacity: isDisabled ? 0.6 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer'
              }}
            />
          </span>
        </Tooltip>
      )
    }
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 220,
    align: 'center',
    render: (_, record) => {
      const reminderKey = record.id || record.rfpId
      const isReminderLoading = !!reminderLoadingByRfpId[reminderKey]
      const isPendingOrPartial = ['Pending', 'Partial'].includes(record.status)
      const isSubmittedRfp = record.saveAsDraft !== true
      const normalizedCreatedByEmail = String(record.createdByEmail || '').trim().toLowerCase()
      const normalizedCurrentUserEmail = String(currentUserEmail || '').trim().toLowerCase()
      const normalizedCreatedByName = String(record.createdBy || '').trim().toLowerCase()
      const normalizedCurrentUserName = String(currentUserName || '').trim().toLowerCase()

      const hasCreatorEmail = Boolean(normalizedCreatedByEmail)
      const hasCreatorName = Boolean(normalizedCreatedByName)

      const isCreatorByEmail =
        hasCreatorEmail &&
        Boolean(normalizedCurrentUserEmail) &&
        normalizedCreatedByEmail === normalizedCurrentUserEmail

      const isCreatorByName =
        !hasCreatorEmail &&
        hasCreatorName &&
        Boolean(normalizedCurrentUserName) &&
        normalizedCreatedByName === normalizedCurrentUserName

      const isCreator = isCreatorByEmail || isCreatorByName

      const shouldShowReminderIcon = isSubmittedRfp && isPendingOrPartial && isCreator

      return (
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
        <Tooltip title="View Cost Center & Purpose">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              transition: 'background-color 0.3s'
            }}
            onClick={() => handleCostCenterClick(record)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(173, 2, 112, 0.05)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <BankOutlined style={{ color: '#ad0270', fontSize: '18px' }} />
          </div>
        </Tooltip>

        <Tooltip
          title={
            record.attachments && record.attachments.length > 0
              ? `View ${record.attachments.length} Attachment${record.attachments.length > 1 ? 's' : ''}`
              : 'No Attachments'
          }
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              transition: 'background-color 0.3s'
            }}
            onClick={() => onAttachmentClick && onAttachmentClick(record)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.05)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <PaperClipOutlined
              style={{
                fontSize: '18px',
                color: record.attachments && record.attachments.length > 0 ? '#1890ff' : '#8c8c8c'
              }}
            />
          </div>
        </Tooltip>

        {shouldShowReminderIcon && (
          <Tooltip title={isReminderLoading ? 'Sending reminder...' : 'Send follow-up reminder'}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: isReminderLoading ? 'not-allowed' : 'pointer',
                padding: '4px',
                borderRadius: '4px',
                transition: 'background-color 0.3s',
                opacity: isReminderLoading ? 0.6 : 1,
                pointerEvents: isReminderLoading ? 'none' : 'auto'
              }}
              onClick={() => !isReminderLoading && onSendReminder && onSendReminder(record)}
              onMouseEnter={(e) => {
                if (!isReminderLoading) {
                  e.currentTarget.style.backgroundColor = 'rgba(250, 173, 20, 0.08)'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              {isReminderLoading ? (
                <LoadingOutlined spin style={{ fontSize: '18px', color: '#faad14' }} />
              ) : (
                <BellOutlined style={{ fontSize: '18px', color: '#faad14' }} />
              )}
            </div>
          </Tooltip>
        )}

        {record.getApproval && (
          <>
            <Tooltip title="Approve">
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  border: '1px solid #52c41a',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#52c41a',
                  transition: 'all 0.3s'
                }}
                onClick={() => handleApproveRFP(record, msalInstance, onRefresh)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#52c41a'
                  e.currentTarget.style.color = '#fff'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = '#52c41a'
                }}
              >
                ✓
              </div>
            </Tooltip>

            <Tooltip title="Reject">
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  border: '1px solid #ff4d4f',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#ff4d4f',
                  transition: 'all 0.3s'
                }}
                onClick={() => handleRejectRFP(record, msalInstance, onRefresh)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#ff4d4f'
                  e.currentTarget.style.color = '#fff'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = '#ff4d4f'
                }}
              >
                ×
              </div>
            </Tooltip>
          </>
        )}
      </div>
      )
    }
  }
]
